import { ExternalLink as ExternalIcon } from 'lucide-react';
import type { ComponentPropsWithoutRef } from 'react';
import { cx } from '../../utils/cx';
import { Icon } from '../Icon/Icon';
import s from './ExternalLink.module.css';

export type ExternalLinkProps = Omit<ComponentPropsWithoutRef<'a'>, 'target' | 'rel'> & {
  href: string;
};

/** Only http(s) URLs render as links (never javascript:, data:, …). */
export const isSafeUrl = (href: string) => {
  try {
    const u = new URL(href);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
};

/** Opens in a new tab with noopener/noreferrer and a ↗ icon. Unsafe URLs render as plain text. */
export function ExternalLink({ href, className, children, ...rest }: ExternalLinkProps) {
  if (!isSafeUrl(href)) return <span className={className}>{children}</span>;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={cx(s.link, className)}
      {...rest}
    >
      {children}
      <Icon icon={ExternalIcon} size="sm" className={s.icon} />
      <span className={s.sr}> (opens in a new tab)</span>
    </a>
  );
}
